// components/ProposalDraftModal.tsx - Modernized with Source-Grounded Support
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  X,
  Download,
  Save,
  ChevronRight,
  FileText,
  Zap,
  Building2,
  Menu,
  RefreshCw,
  Eye,
  Edit2,
  RotateCw,
  ChevronDown,
  Link2,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Database,
  Target,
  Map as MapIcon,
  ExternalLink,
  ArrowRight,
  Star,
  Sparkles,
  Info,
  Package,
} from 'lucide-react';
import { GenerationProgressFloat, type GenerationProgress, type GenerationStep } from './proposal/GenerationProgressFloat';
import { Contract } from '../types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PreviewComponents } from './shared/MarkdownComponents';
import { ProposalExportService } from '../services/ProposalExportService';
import { BeautifulDocxExporter } from '../services/BeautifulDocxExporter';
import { smartNormalize, formatCoverLetter } from '../lib/markdown-utils';
import { CitationHighlight } from './CitationPopover';
import { ProposalSectionRenderer, AIFeedbackPanel, SectionFeedbackDialog, ProfileSourceModal, DeliverablesSectionContent, DeliverableFeedbackDialog, ProductFeedbackDialog, ExportDialog } from './proposal';
import '../styles/print.css';
import type { DeliverableFeedback } from './proposal/DeliverableFeedbackDialog';
import type { ProductFeedback } from './proposal/ProductFeedbackDialog';
import { ProductSynthesis } from './proposal/ProductSynthesis';
import type { DeliverablesData, ProductDefinition } from '../types/deliverables';
import type { SectionFeedback } from './proposal/SectionFeedbackDialog';
import { supabase } from '../lib/supabase';
import type { AIFeedback } from '../services/orchestrated-proposal-service';
import {
  SourceGroundedProposalOutput,
  GapReport,
  SourceMap,
  ScoredEvidenceItem,
  WinAssessment,
  ImprovementRoadmap,
} from '../types/source-grounded';

// ============================================================
// TYPES
// ============================================================

interface ProposalContent {
  coverLetter: string;
  technicalSections: Array<{
    title: string;
    content: string;
  }>;
  feedback: string;
  contractAnalysis: string;
  profileAnalysis: string;
  message: string;
  aiFeedback?: AIFeedback | null;
  deliverablesData?: DeliverablesData | null;
}

interface ProposalDraftModalProps {
  draft: ProposalContent;
  contract?: Contract;
  onClose: () => void;
  onUpdate: (draft: ProposalContent) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onExport: (id: string) => Promise<void>;
  onRecalculatePricing?: () => Promise<void>;
  onRegenerateProposal?: () => Promise<void>;
  // Source-grounded specific props
  sourceGroundedOutput?: SourceGroundedProposalOutput;
  onNavigateToProfile?: (section: string) => void;
}

// ============================================================
// SIMPLE FEEDBACK DISPLAY (for edge function format)
// ============================================================

function SimpleFeedbackDisplay({ feedback }: { feedback: any }) {
  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-success bg-success-bg';
    if (score >= 70) return 'text-bw-primary bg-bw-primary-tint';
    if (score >= 55) return 'text-fit-moderate bg-fit-moderate-bg';
    return 'text-urgent bg-urgent-bg';
  };

  const overallScore = feedback.overallScore || 0;
  const strengths = feedback.strengths || [];
  const recommendations = feedback.recommendations || [];
  const volumeScores = feedback.volumeScores || {};
  const winProbability = feedback.winProbability || 'Unknown';
  const complianceScore = feedback.complianceScore || 0;

  return (
    <div className="space-y-6">
      {/* Overall Score Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className={`rounded-lg p-4 ${getScoreColor(overallScore)}`}>
          <p className="text-sm font-medium opacity-80">Overall Score</p>
          <p className="text-3xl font-bold">{overallScore}</p>
        </div>
        <div className="bg-bw-primary-tint rounded-lg p-4">
          <p className="text-sm text-bw-primary font-medium">Win Probability</p>
          <p className="text-2xl font-bold text-bw-primary">{winProbability}</p>
        </div>
        <div className={`rounded-lg p-4 ${getScoreColor(complianceScore)}`}>
          <p className="text-sm font-medium opacity-80">Compliance</p>
          <p className="text-3xl font-bold">{complianceScore}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-4">
          <p className="text-sm text-slate-600 font-medium">Volumes</p>
          <p className="text-3xl font-bold text-slate-700">{feedback.volumesGenerated || 0}</p>
        </div>
      </div>

      {/* Volume Scores */}
      {Object.keys(volumeScores).length > 0 && (
        <div className="bg-bw-surface rounded-xl border border-bw-border p-6">
          <h3 className="text-lg font-semibold text-bw-ink mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-bw-primary" />
            Volume Scores
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(volumeScores).map(([key, score]: [string, any]) => (
              <div key={key} className="text-center p-3 bg-slate-50 rounded-lg">
                <div className={`text-2xl font-bold ${score >= 80 ? 'text-success' : score >= 60 ? 'text-fit-moderate' : 'text-urgent'}`}>
                  {score}
                </div>
                <div className="text-xs text-bw-muted capitalize mt-1">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strengths */}
      {strengths.length > 0 && (
        <div className="bg-success-bg rounded-xl border border-green-200 p-6">
          <h3 className="text-lg font-semibold text-success mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Strengths
          </h3>
          <ul className="space-y-2">
            {strengths.map((strength: string, i: number) => (
              <li key={i} className="flex items-start gap-2 text-success">
                <span className="text-success mt-1">✓</span>
                {strength}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-fit-moderate-bg rounded-xl border border-amber-200 p-6">
          <h3 className="text-lg font-semibold text-fit-moderate mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((rec: string, i: number) => (
              <li key={i} className="flex items-start gap-2 text-fit-moderate">
                <span className="text-fit-moderate mt-1">→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Page Estimate */}
      {feedback.pageEstimate && (
        <div className="bg-slate-100 rounded-lg p-4 text-center">
          <p className="text-sm text-slate-600">Estimated Total Pages</p>
          <p className="text-2xl font-bold text-slate-800">{feedback.pageEstimate}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPONENT
// ============================================================

export function ProposalDraftModal({
  draft,
  contract,
  onClose,
  onUpdate,
  onDelete,
  onExport,
  onRecalculatePricing,
  onRegenerateProposal,
  sourceGroundedOutput,
  onNavigateToProfile,
}: ProposalDraftModalProps) {
  const { profile } = useAuth();

  // UI State
  const [activeTab, setActiveTab] = useState('coverLetter');
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editedContent, setEditedContent] = useState(draft);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sync editedContent with draft prop when draft changes (e.g., on load from DB)
  // This ensures deliverablesData and other fields are properly loaded
  useEffect(() => {
    setEditedContent(draft);
  }, [draft]);

  // Section regeneration state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showProfileSourceModal, setShowProfileSourceModal] = useState(false);
  const [regeneratingSectionType, setRegeneratingSectionType] = useState<string | null>(null);
  const [regeneratingSectionTitle, setRegeneratingSectionTitle] = useState<string>('');

  // Source-grounded specific state
  const [showCitations, setShowCitations] = useState(true);
  const [activeSection, setActiveSection] = useState<'content' | 'gaps' | 'sources' | 'evidence' | 'roadmap'>('content');

  // Deliverables enhancement state
  const [enhancingDeliverable, setEnhancingDeliverable] = useState(false);
  const [enhancingDeliverableId, setEnhancingDeliverableId] = useState<string | null>(null);
  const [regeneratingDeliverables, setRegeneratingDeliverables] = useState(false);

  // Section enhancement dialogs state
  const [showDeliverableFeedbackDialog, setShowDeliverableFeedbackDialog] = useState(false);
  const [showProductFeedbackDialog, setShowProductFeedbackDialog] = useState(false);
  const [showIndividualDeliverableFeedback, setShowIndividualDeliverableFeedback] = useState(false);
  const [selectedDeliverableForEnhancement, setSelectedDeliverableForEnhancement] = useState<{ id: string; name: string } | null>(null);
  const [enhancingDeliverableSection, setEnhancingDeliverableSection] = useState(false);
  const [enhancingProductSection, setEnhancingProductSection] = useState(false);

  // Background generation progress state
  const [generationProgress, setGenerationProgress] = useState<GenerationProgress | null>(null);

  // Refs
  const mobileNavRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Detect if this is a source-grounded proposal
  const isSourceGrounded = useMemo(() => {
    return !!sourceGroundedOutput || draft.coverLetter?.includes('[PP-') || draft.coverLetter?.includes('[PERSONNEL-');
  }, [sourceGroundedOutput, draft]);

  // Detect if this is an orchestrated proposal (has aiFeedback or proposalType in content)
  const isOrchestrated = useMemo(() => {
    return !!draft.aiFeedback ||
           (draft as any).proposalType === 'orchestrated' ||
           // Check if technicalSections indicate orchestrated format
           (draft.technicalSections?.some(s =>
             s.title === 'Technical Approach' ||
             s.title === 'Management Approach' ||
             s.title === 'Key Personnel' ||
             s.title === 'Pricing & Cost Strategy' ||
             s.title === 'FAR/DFARS Compliance'
           ));
  }, [draft]);

  // Extract source map data
  const sourceMap = sourceGroundedOutput?.sourceMap;
  const gapReport = sourceGroundedOutput?.gapReport;
  const winAssessment = sourceGroundedOutput?.winAssessment;
  const improvementRoadmap = sourceGroundedOutput?.improvementRoadmap;

  // Extract orchestrated proposal gap data (from feedback field)
  const orchestratedFeedback = useMemo(() => {
    const feedback = (draft as any)?.feedback;
    if (feedback?.type === 'orchestrated') {
      return {
        readinessScore: feedback.readinessScore || 0,
        gaps: feedback.gaps || { critical: [], recommended: [], optional: [] },
        matches: feedback.matches || {},
      };
    }
    return null;
  }, [draft]);

  // Build evidence map for citation lookups
  const evidenceMap = useMemo(() => {
    const map = new Map<string, ScoredEvidenceItem>();
    return map;
  }, [sourceGroundedOutput]);

  // Build citations array
  const allCitations = useMemo(() => {
    return sourceMap?.allCitations?.map(c => ({
      textSpan: c.textSpan || '',
      evidenceId: c.evidenceId,
      evidenceType: c.evidenceType as any,
      evidenceSummary: c.evidenceSummary,
      isInferred: c.matchType === 'INFERRED',
      inferenceChain: c.inferenceChain,
    })) || [];
  }, [sourceMap]);

  // Close handlers
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target as Node)) {
        setShowMobileNav(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    }

    if (showMobileNav || showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMobileNav, showExportMenu]);

  useEffect(() => {
    setShowMobileNav(false);
  }, [activeTab]);

  // Tab configuration - different for source-grounded, orchestrated, vs basic/enhanced
  // Deliverables tab should appear AFTER Key Personnel but BEFORE Pricing & Compliance
  const contentTabs = useMemo(() => {
    // Sections that come BEFORE Deliverables (in order)
    const sectionsBeforeDeliverables = ['executive summary', 'technical approach', 'management approach', 'past performance', 'key personnel'];
    // Sections that come AFTER Deliverables (in order)
    const sectionsAfterDeliverables = ['pricing & cost strategy', 'far/dfars compliance'];

    // Map technical sections to tabs
    const allTechnicalTabs = (draft.technicalSections || []).map(section => ({
      id: section.title.toLowerCase().replace(/\s+/g, '_'),
      label: section.title,
      icon: Zap,
      originalTitle: section.title.toLowerCase(),
    }));

    // Split into before/after deliverables
    const beforeDeliverables = allTechnicalTabs.filter(tab =>
      sectionsBeforeDeliverables.includes(tab.originalTitle)
    );
    const afterDeliverables = allTechnicalTabs.filter(tab =>
      sectionsAfterDeliverables.includes(tab.originalTitle)
    );
    // Any sections not explicitly categorized go before deliverables
    const uncategorized = allTechnicalTabs.filter(tab =>
      !sectionsBeforeDeliverables.includes(tab.originalTitle) &&
      !sectionsAfterDeliverables.includes(tab.originalTitle)
    );

    // Build base tabs: Cover Letter + sections before deliverables + uncategorized
    const baseTabs = [
      { id: 'coverLetter', label: 'Cover Letter', icon: FileText },
      ...beforeDeliverables,
      ...uncategorized,
    ];

    // Deliverables tab - always show it so users know deliverables analysis is available
    console.log('📦 Deliverables tab check:', {
      hasDataInDraft: !!draft.deliverablesData,
      hasDataInEdited: !!editedContent.deliverablesData,
      deliverableCount: editedContent.deliverablesData?.deliverables_with_labor?.length || 0
    });
    const deliverablesTab = { id: 'deliverables', label: 'Deliverables', icon: Package };

    // Product tab - AI synthesizes what we're selling from deliverables
    const productTab = { id: 'product', label: 'Product', icon: Sparkles };

    // Source-grounded: base tabs + deliverables + product + pricing/compliance sections + AI Feedback
    if (isSourceGrounded) {
      return [
        ...baseTabs,
        deliverablesTab,
        productTab,
        ...afterDeliverables,
        { id: 'feedback', label: 'AI Feedback', icon: Zap },
      ];
    }

    // Orchestrated: base tabs + deliverables + product + pricing/compliance sections + AI Feedback
    if (isOrchestrated) {
      return [
        ...baseTabs,
        deliverablesTab,
        productTab,
        ...afterDeliverables,
        { id: 'feedback', label: 'AI Feedback', icon: Zap },
      ];
    }

    // Legacy/basic: all tabs with deliverables and product in correct position
    return [
      ...baseTabs,
      deliverablesTab,
      productTab,
      ...afterDeliverables,
      { id: 'contractAnalysis', label: 'Contract Analysis', icon: Building2 },
      { id: 'profileAnalysis', label: 'Profile Analysis', icon: Target },
      { id: 'feedback', label: 'AI Feedback', icon: Zap },
    ];
  }, [draft, editedContent, isSourceGrounded, isOrchestrated]);

  // Handlers
  const handleSaveChanges = async () => {
    try {
      setUpdating(true);
      await onUpdate(editedContent);
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      toast.success('Changes saved');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setUpdating(false);
    }
  };

  const handleRegenerateProposal = async () => {
    if (!onRegenerateProposal) return;
    if (!window.confirm('This will replace the current proposal. Continue?')) return;

    try {
      setRegenerating(true);
      await onRegenerateProposal();
      toast.success('Proposal regenerated');
    } catch (error) {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const handleExportProposal = async (format: 'pdf' | 'word' | 'markdown', includeCitations: boolean = true) => {
    if (!contract) {
      toast.error('Contract information missing');
      return;
    }

    try {
      setExporting(true);
      setShowExportMenu(false);

      const exportDraft = includeCitations ? draft : stripCitations(draft);

      switch (format) {
        case 'pdf':
          await ProposalExportService.exportToPDF(exportDraft, contract);
          break;
        case 'word':
          // Use beautiful exporter for orchestrated proposals to match UI
          if (isOrchestrated) {
            await BeautifulDocxExporter.export(exportDraft, contract);
          } else {
            await ProposalExportService.exportToWord(exportDraft, contract);
          }
          break;
        case 'markdown':
          await ProposalExportService.exportToMarkdown(exportDraft, contract);
          break;
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const stripCitations = (content: ProposalContent): ProposalContent => {
    const stripText = (text: string) =>
      text.replace(/\s*\[[A-Z]+-\d+(?:,\s*[^\]]+)?]\s*/gi, ' ').replace(/\s+/g, ' ');

    return {
      ...content,
      coverLetter: stripText(content.coverLetter),
      technicalSections: content.technicalSections.map(s => ({
        ...s,
        content: stripText(s.content),
      })),
      feedback: stripText(content.feedback),
      contractAnalysis: stripText(content.contractAnalysis),
      profileAnalysis: stripText(content.profileAnalysis),
      message: content.message,
    };
  };

  const getContent = (tabId: string) => {
    // Always use editedContent since it's initialized from draft and tracks all changes
    // This ensures regenerated content displays correctly even after auto-save
    const source = editedContent;
    switch (tabId) {
      case 'contractAnalysis':
        return source.contractAnalysis;
      case 'profileAnalysis':
        return source.profileAnalysis;
      case 'coverLetter':
        return source.coverLetter;
      case 'feedback':
        return source.feedback;
      default:
        const section = source.technicalSections.find(
          s => s.title.toLowerCase().replace(/\s+/g, '_') === tabId
        );
        return section?.content || '';
    }
  };

  const handleNavigateToProfile = (section: string) => {
    if (onNavigateToProfile) {
      onNavigateToProfile(section);
    } else {
      window.location.hash = `#profile/${section}`;
    }
    onClose();
  };

  // Open feedback dialog for section regeneration
  const handleOpenFeedbackDialog = (sectionType: string, sectionTitle: string) => {
    setRegeneratingSectionType(sectionType);
    setRegeneratingSectionTitle(sectionTitle);
    setShowFeedbackDialog(true);
  };

  // Handle section regeneration with user feedback
  const handleRegenerateSection = async (feedback: SectionFeedback) => {
    if (!regeneratingSectionType || !contract) return;

    setRegenerating(true);
    console.log('🔄 Starting section regeneration:', {
      sectionType: feedback.sectionType,
      sectionTitle: regeneratingSectionTitle,
      enhancementLevel: feedback.enhancementLevel,
      feedbackLength: feedback.userFeedback?.length,
    });

    try {
      // Get current content for this section
      const currentContent = getContent(regeneratingSectionType);
      console.log('📄 Current content retrieved:', {
        sectionType: regeneratingSectionType,
        contentLength: currentContent?.length || 0,
        contentPreview: currentContent?.substring(0, 100),
      });

      // Build context from existing data
      const originalContext = {
        company: {
          name: profile?.company_name || null,
          valueProposition: profile?.value_proposition || null,
          capabilities: profile?.capabilities?.map((c: any) => c.name) || [],
        },
        contract: {
          title: contract.title,
          agency: contract.agency,
          naicsCode: contract.naics_code || null,
        },
      };

      // Call the regenerate-section edge function
      console.log('🚀 Calling regenerate-section edge function...');
      const { data, error } = await supabase.functions.invoke('regenerate-section', {
        body: {
          sectionType: feedback.sectionType,
          currentContent,
          userFeedback: feedback.userFeedback,
          originalContext,
          enhancementLevel: feedback.enhancementLevel,
        },
      });

      console.log('📥 Edge function response:', {
        success: data?.success,
        error: error?.message || data?.error,
        contentLength: data?.content?.length,
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Regeneration failed');

      // Update the section content
      const newContent = { ...editedContent };

      if (feedback.sectionType === 'coverLetter' || feedback.sectionType === 'cover_letter') {
        console.log('✏️ Updating cover letter');
        newContent.coverLetter = data.content;
      } else {
        // Find the matching technical section
        console.log('🔍 Looking for section in technicalSections:', {
          feedbackType: feedback.sectionType,
          sectionTitle: regeneratingSectionTitle,
          availableSections: newContent.technicalSections.map(s => ({
            title: s.title,
            normalized: s.title.toLowerCase().replace(/\s+/g, '_'),
          })),
        });

        const sectionIdx = newContent.technicalSections.findIndex(
          s => s.title.toLowerCase().replace(/\s+/g, '_') === feedback.sectionType ||
               s.title === regeneratingSectionTitle
        );

        console.log('📍 Section index found:', sectionIdx);

        if (sectionIdx !== -1) {
          newContent.technicalSections[sectionIdx] = {
            ...newContent.technicalSections[sectionIdx],
            content: data.content,
          };
          console.log('✅ Section content updated:', newContent.technicalSections[sectionIdx].title);
        } else {
          console.error('❌ Section not found! Could not update content.');
          toast.error(`Could not find section "${regeneratingSectionTitle}" to update`);
          return;
        }
      }

      setEditedContent(newContent);

      // Auto-save the changes
      console.log('💾 Auto-saving changes...');
      await onUpdate(newContent);

      // Reset hasUnsavedChanges since we just saved
      setHasUnsavedChanges(false);

      toast.success(`${regeneratingSectionTitle} regenerated successfully!`);
      setShowFeedbackDialog(false);
      console.log('✅ Regeneration complete for:', regeneratingSectionTitle);

    } catch (error: any) {
      console.error('❌ Section regeneration error:', error);
      toast.error(`Failed to regenerate: ${error.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  // Get the current section title for feedback dialog
  const getCurrentSectionTitle = () => {
    if (activeTab === 'coverLetter') return 'Cover Letter';
    const section = draft.technicalSections.find(
      s => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab
    );
    return section?.title || activeTab;
  };

  // Handle enhancing individual deliverable sections
  const handleEnhanceDeliverableSection = async (deliverableId: string) => {
    if (!contract || !editedContent.deliverablesData) return;

    setEnhancingDeliverable(true);
    setEnhancingDeliverableId(deliverableId);

    try {
      const currentDeliverable = editedContent.deliverablesData.deliverables_with_labor.find(
        d => d.deliverable_id === deliverableId
      );

      if (!currentDeliverable) {
        throw new Error('Deliverable not found');
      }

      const { data, error } = await supabase.functions.invoke('enhance-deliverable-section', {
        body: {
          deliverable_id: deliverableId,
          current_content: {
            deliverable_name: currentDeliverable.deliverable_name,
            description: currentDeliverable.description,
            type: currentDeliverable.type,
            labor_phases: currentDeliverable.labor_phases,
            total_hours: currentDeliverable.total_hours,
            specifications: currentDeliverable.specifications,
            compliance_requirements: currentDeliverable.compliance_requirements,
          },
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: contract.title,
            agency: contract.agency,
          },
          enhancement_type: 'all',
        },
      });

      if (error) throw error;

      if (data?.success && data?.enhanced_deliverable) {
        // Update the deliverable in the local state
        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          deliverables_with_labor: editedContent.deliverablesData.deliverables_with_labor.map(d =>
            d.deliverable_id === deliverableId
              ? { ...d, ...data.enhanced_deliverable }
              : d
          ),
        };

        const newContent = {
          ...editedContent,
          deliverablesData: newDeliverablesData,
        };

        setEditedContent(newContent);

        // Auto-save
        await onUpdate(newContent);
        toast.success(`Enhanced ${currentDeliverable.deliverable_name}`);
      }
    } catch (error: any) {
      console.error('❌ Deliverable enhancement error:', error);
      toast.error(`Failed to enhance deliverable: ${error.message}`);
    } finally {
      setEnhancingDeliverable(false);
      setEnhancingDeliverableId(null);
    }
  };

  // Handle regenerating all deliverables (e.g., after adding new documents)
  // Now with background progress tracking
  const handleRegenerateDeliverables = async () => {
    if (!contract) return;

    const contractId = contract.noticeId || contract.id;

    // Initialize progress tracking
    setGenerationProgress({
      step: 'extracting',
      contractTitle: contract.title,
      contractId,
    });
    setRegeneratingDeliverables(true);
    console.log('🔄 Regenerating deliverables for contract:', contract.title);

    try {
      // Step 1: Extract raw deliverables from documents
      console.log('📄 Step 1: Extracting deliverables from RFQ...');
      const { data: extractedData, error: extractError } = await supabase.functions.invoke('extract-deliverables-raw', {
        body: {
          rfq_documents: contract.attachments || [],
          contract_metadata: {
            notice_id: contractId,
            title: contract.title,
            contract_type: contract.type || 'service',
            naics_code: contract.naicsCode || contract.naics_code,
            agency: contract.agency,
            description: contract.description,
          },
        },
      });

      if (extractError) throw extractError;

      if (!extractedData?.deliverables?.length) {
        setGenerationProgress(prev => prev ? { ...prev, step: 'error', error: 'No deliverables found in documents. Add RFQ documents with deliverable requirements.' } : null);
        toast.error('No deliverables found in documents. Add RFQ documents with deliverable requirements.');
        setRegeneratingDeliverables(false);
        return;
      }

      console.log(`✅ Extracted ${extractedData.deliverables.length} deliverables`);

      // Step 2: Enrich with compliance requirements
      setGenerationProgress(prev => prev ? { ...prev, step: 'enriching' } : null);
      console.log('📋 Step 2: Enriching with compliance requirements...');
      const { data: enrichedData, error: enrichError } = await supabase.functions.invoke('enrich-deliverables-compliance', {
        body: {
          extracted_deliverables: extractedData,
          contract_metadata: {
            notice_id: contractId,
            title: contract.title,
            contract_type: contract.type || 'service',
            naics_code: contract.naicsCode || contract.naics_code,
            agency: contract.agency,
          },
        },
      });

      if (enrichError) throw enrichError;
      console.log('✅ Compliance enrichment complete');

      // Step 3: Generate labor framework
      setGenerationProgress(prev => prev ? { ...prev, step: 'labor' } : null);
      console.log('💼 Step 3: Generating labor framework...');
      const { data: laborData, error: laborError } = await supabase.functions.invoke('generate-labor-framework', {
        body: {
          enriched_deliverables: enrichedData,
          user_profile: {
            labor_categories: profile?.pricing_data,
          },
          contract_constraints: {
            period_months: 12,
            hours_ceiling: contract.ceiling_hours,
            budget_ceiling: contract.estimated_value || contract.fundingAmount,
          },
        },
      });

      if (laborError) throw laborError;
      console.log('✅ Labor framework generated');

      // Step 4: Synthesize product definition
      setGenerationProgress(prev => prev ? { ...prev, step: 'synthesizing' } : null);
      console.log('✨ Step 4: Synthesizing product definition...');

      let productDefinition = null;
      try {
        // Map deliverables_with_labor to the format expected by synthesize-product
        const deliverables = laborData.deliverables_with_labor.map((d: any) => ({
          id: d.deliverable_id || d.id,
          name: d.deliverable_name || d.name,
          description: d.description || '',
          type: d.type || 'service',
          category: d.category,
          quantity: d.quantity || 1,
          unit: d.unit || 'each',
          specifications: d.specifications,
          labor_phases: d.labor_phases,
          total_hours: d.total_hours,
          compliance_requirements: d.compliance_requirements,
        }));

        // Map compliance framework to compliance_requirements if available
        const complianceFramework = laborData.compliance_framework || enrichedData.compliance_framework;
        const compliance_requirements = complianceFramework?.requirements?.map((r: any) => ({
          id: r.id || r.requirement_id,
          name: r.name || r.requirement,
          category: r.category || 'general',
          description: r.description || r.details || '',
        })) || [];

        const { data: productData, error: productError } = await supabase.functions.invoke('synthesize-product', {
          body: {
            deliverables,
            compliance_requirements,
            labor_summary: laborData.labor_summary,
            contract_metadata: {
              notice_id: contractId,
              title: contract.title,
              contract_type: contract.type || 'service',
              naics_code: contract.naicsCode || contract.naics_code || '',
              agency: contract.agency,
              description: contract.description,
            },
          },
        });

        if (!productError && productData?.product_definition) {
          productDefinition = productData.product_definition;
          console.log('✅ Product synthesis complete');
        } else {
          console.warn('⚠️ Product synthesis skipped:', productError?.message || 'No product definition returned');
        }
      } catch (synthError: any) {
        console.warn('⚠️ Product synthesis failed (non-fatal):', synthError.message);
        // Continue without product synthesis - it's optional
      }

      // Build new deliverables data
      const newDeliverablesData = {
        deliverables_with_labor: laborData.deliverables_with_labor || [],
        labor_summary: laborData.labor_summary,
        pricing_inputs: laborData.pricing_inputs,
        compliance_framework: laborData.compliance_framework || enrichedData.compliance_framework,
        timeline_summary: extractedData.timeline_summary,
        volume_summary: extractedData.volume_summary,
        product_definition: productDefinition,
        metadata: {
          regeneratedAt: new Date().toISOString(),
          deliverablesCount: laborData.deliverables_with_labor?.length || 0,
        },
      };

      // Update content
      const newContent = {
        ...editedContent,
        deliverablesData: newDeliverablesData,
      };

      setEditedContent(newContent);

      // Auto-save
      await onUpdate(newContent);

      // Mark complete with results
      setGenerationProgress(prev => prev ? {
        ...prev,
        step: 'complete',
        result: {
          deliverablesCount: newDeliverablesData.deliverables_with_labor.length,
          componentsCount: productDefinition?.components?.length,
        },
      } : null);

      toast.success(`Generated ${newDeliverablesData.deliverables_with_labor.length} deliverables with product synthesis`);
      console.log('✅ Deliverables regeneration complete');

    } catch (error: any) {
      console.error('❌ Deliverables regeneration error:', error);
      setGenerationProgress(prev => prev ? { ...prev, step: 'error', error: error.message } : null);
      toast.error(`Failed to regenerate deliverables: ${error.message}`);
    } finally {
      setRegeneratingDeliverables(false);
    }
  };

  // Synthesize product only (when deliverables already exist)
  const handleSynthesizeProduct = async () => {
    if (!contract || !editedContent.deliverablesData) {
      toast.error('Generate deliverables first before synthesizing product');
      return;
    }

    const contractId = contract.noticeId || contract.id;
    setRegeneratingDeliverables(true);
    setGenerationProgress({
      step: 'synthesizing',
      contractTitle: contract.title,
      contractId,
    });

    try {
      console.log('✨ Synthesizing product definition...');

      // Map deliverables_with_labor to the format expected by synthesize-product
      const deliverables = editedContent.deliverablesData.deliverables_with_labor.map((d: any) => ({
        id: d.deliverable_id || d.id,
        name: d.deliverable_name || d.name,
        description: d.description || '',
        type: d.type || 'service',
        category: d.category,
        quantity: d.quantity || 1,
        unit: d.unit || 'each',
        specifications: d.specifications,
        labor_phases: d.labor_phases,
        total_hours: d.total_hours,
        compliance_requirements: d.compliance_requirements,
      }));

      // Map compliance framework to compliance_requirements if available
      const compliance_requirements = editedContent.deliverablesData.compliance_framework?.requirements?.map((r: any) => ({
        id: r.id || r.requirement_id,
        name: r.name || r.requirement,
        category: r.category || 'general',
        description: r.description || r.details || '',
      })) || [];

      const { data: productData, error: productError } = await supabase.functions.invoke('synthesize-product', {
        body: {
          deliverables,
          compliance_requirements,
          labor_summary: editedContent.deliverablesData.labor_summary,
          contract_metadata: {
            notice_id: contractId,
            title: contract.title,
            contract_type: contract.type || 'service',
            naics_code: contract.naicsCode || contract.naics_code || '',
            agency: contract.agency,
            description: contract.description,
          },
        },
      });

      if (productError) throw productError;

      if (!productData?.product_definition) {
        throw new Error('No product definition returned from synthesis');
      }

      console.log('✅ Product synthesis complete');

      // Update deliverablesData with product_definition
      const newDeliverablesData = {
        ...editedContent.deliverablesData,
        product_definition: productData.product_definition,
      };

      const newContent = {
        ...editedContent,
        deliverablesData: newDeliverablesData,
      };

      setEditedContent(newContent);

      // Save to database
      console.log('💾 Saving to database...');
      await onUpdate(newContent);
      console.log('✅ Saved to database');

      setGenerationProgress(prev => prev ? {
        ...prev,
        step: 'complete',
        result: {
          deliverablesCount: newDeliverablesData.deliverables_with_labor?.length || 0,
          componentsCount: productData.product_definition.components?.length,
        },
      } : null);

      toast.success('Product synthesis complete!');

    } catch (error: any) {
      console.error('❌ Product synthesis error:', error);
      setGenerationProgress(prev => prev ? { ...prev, step: 'error', error: error.message } : null);
      toast.error(`Failed to synthesize product: ${error.message}`);
    } finally {
      setRegeneratingDeliverables(false);
    }
  };

  // Open deliverables section enhancement dialog
  const handleOpenDeliverablesFeedback = () => {
    setShowDeliverableFeedbackDialog(true);
  };

  // Open product section enhancement dialog
  const handleOpenProductFeedback = () => {
    setShowProductFeedbackDialog(true);
  };

  // Open individual deliverable enhancement dialog
  const handleOpenIndividualDeliverableFeedback = (deliverableId: string) => {
    const deliverable = editedContent.deliverablesData?.deliverables_with_labor?.find(
      d => d.deliverable_id === deliverableId
    );
    if (deliverable) {
      setSelectedDeliverableForEnhancement({
        id: deliverableId,
        name: deliverable.deliverable_name,
      });
      setShowIndividualDeliverableFeedback(true);
    }
  };

  // Handle deliverables section enhancement with user feedback
  const handleEnhanceDeliverablesSection = async (feedback: DeliverableFeedback) => {
    if (!contract || !editedContent.deliverablesData) return;

    setEnhancingDeliverableSection(true);
    console.log('🚀 Starting deliverables section enhancement:', feedback);

    try {
      const { data, error } = await supabase.functions.invoke('enhance-deliverables-section', {
        body: {
          deliverables_data: {
            deliverables_with_labor: editedContent.deliverablesData.deliverables_with_labor,
            labor_summary: editedContent.deliverablesData.labor_summary,
            pricing_inputs: editedContent.deliverablesData.pricing_inputs,
            compliance_framework: editedContent.deliverablesData.compliance_framework,
            timeline_summary: editedContent.deliverablesData.timeline_summary,
          },
          user_feedback: feedback.userFeedback,
          enhancement_level: feedback.enhancementLevel,
          focus_areas: feedback.focusAreas,
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: contract.title,
            agency: contract.agency,
            naics_code: contract.naicsCode || contract.naics_code,
          },
        },
      });

      if (error) throw error;

      if (data?.success && data?.enhanced_deliverables) {
        // Update deliverables data
        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          deliverables_with_labor: data.enhanced_deliverables,
          labor_summary: data.updated_labor_summary || editedContent.deliverablesData.labor_summary,
        };

        const newContent = {
          ...editedContent,
          deliverablesData: newDeliverablesData,
        };

        setEditedContent(newContent);

        // Auto-save
        await onUpdate(newContent);

        toast.success(`Enhanced ${data.enhanced_deliverables.length} deliverables`);
        setShowDeliverableFeedbackDialog(false);
      }
    } catch (error: any) {
      console.error('❌ Deliverables section enhancement error:', error);
      toast.error(`Failed to enhance deliverables: ${error.message}`);
    } finally {
      setEnhancingDeliverableSection(false);
    }
  };

  // Handle individual deliverable enhancement with user feedback
  const handleEnhanceIndividualDeliverable = async (feedback: DeliverableFeedback) => {
    if (!contract || !editedContent.deliverablesData || !feedback.deliverableId) return;

    setEnhancingDeliverable(true);
    setEnhancingDeliverableId(feedback.deliverableId);

    try {
      const currentDeliverable = editedContent.deliverablesData.deliverables_with_labor.find(
        d => d.deliverable_id === feedback.deliverableId
      );

      if (!currentDeliverable) {
        throw new Error('Deliverable not found');
      }

      const { data, error } = await supabase.functions.invoke('enhance-deliverable-section', {
        body: {
          deliverable_id: feedback.deliverableId,
          current_content: {
            deliverable_name: currentDeliverable.deliverable_name,
            description: currentDeliverable.description,
            type: currentDeliverable.type,
            labor_phases: currentDeliverable.labor_phases,
            total_hours: currentDeliverable.total_hours,
            specifications: currentDeliverable.specifications,
            compliance_requirements: currentDeliverable.compliance_requirements,
          },
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: contract.title,
            agency: contract.agency,
          },
          enhancement_type: feedback.focusAreas.length === 1 ? feedback.focusAreas[0] : 'all',
          user_feedback: feedback.userFeedback,
        },
      });

      if (error) throw error;

      if (data?.success && data?.enhanced_deliverable) {
        // Update the deliverable in the local state
        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          deliverables_with_labor: editedContent.deliverablesData.deliverables_with_labor.map(d =>
            d.deliverable_id === feedback.deliverableId
              ? { ...d, ...data.enhanced_deliverable }
              : d
          ),
        };

        const newContent = {
          ...editedContent,
          deliverablesData: newDeliverablesData,
        };

        setEditedContent(newContent);

        // Auto-save
        await onUpdate(newContent);
        toast.success(`Enhanced ${currentDeliverable.deliverable_name}`);
        setShowIndividualDeliverableFeedback(false);
        setSelectedDeliverableForEnhancement(null);
      }
    } catch (error: any) {
      console.error('❌ Individual deliverable enhancement error:', error);
      toast.error(`Failed to enhance deliverable: ${error.message}`);
    } finally {
      setEnhancingDeliverable(false);
      setEnhancingDeliverableId(null);
    }
  };

  // Handle product section enhancement with user feedback
  const handleEnhanceProductSection = async (feedback: ProductFeedback) => {
    if (!contract || !editedContent.deliverablesData?.product_definition) return;

    setEnhancingProductSection(true);
    console.log('🚀 Starting product section enhancement:', feedback);

    try {
      const { data, error } = await supabase.functions.invoke('enhance-product-section', {
        body: {
          product_definition: editedContent.deliverablesData.product_definition,
          user_feedback: feedback.userFeedback,
          enhancement_level: feedback.enhancementLevel,
          focus_areas: feedback.focusAreas,
          deliverables_context: editedContent.deliverablesData.deliverables_with_labor?.slice(0, 5),
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: contract.title,
            agency: contract.agency,
            naics_code: contract.naicsCode || contract.naics_code,
          },
        },
      });

      if (error) throw error;

      if (data?.success && data?.enhanced_product) {
        // Update product definition
        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          product_definition: data.enhanced_product,
        };

        const newContent = {
          ...editedContent,
          deliverablesData: newDeliverablesData,
        };

        setEditedContent(newContent);

        // Auto-save
        await onUpdate(newContent);

        toast.success('Product definition enhanced successfully');
        setShowProductFeedbackDialog(false);
      }
    } catch (error: any) {
      console.error('❌ Product section enhancement error:', error);
      toast.error(`Failed to enhance product: ${error.message}`);
    } finally {
      setEnhancingProductSection(false);
    }
  };

  // Dismiss the generation progress indicator
  const handleDismissProgress = () => {
    setGenerationProgress(null);
  };

  // Navigate to deliverables tab when generation is complete
  const handleViewGenerationResult = () => {
    setActiveTab('deliverables');
    setGenerationProgress(null);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
    {/* Floating progress indicator for background generation */}
    <GenerationProgressFloat
      progress={generationProgress}
      onDismiss={handleDismissProgress}
      onViewResult={handleViewGenerationResult}
    />

    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-bw-surface rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0">
          {/* Top Bar */}
          <div className="bg-bw-sidebar px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-white truncate">
                  {contract?.title || 'Proposal Draft'}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-slate-300">{contract?.agency}</span>
                  {isSourceGrounded && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-bw-primary-tint0/20 text-purple-200 text-xs font-medium rounded-full">
                      <Link2 className="h-3 w-3" />
                      Source-Grounded
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Mode Toggle */}
                <button
                  onClick={() => setIsEditMode(!isEditMode)}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    isEditMode
                      ? 'bg-fit-moderate-bg0 text-white'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {isEditMode ? <Edit2 className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {isEditMode ? 'Editing' : 'Viewing'}
                </button>

                {/* Save Button */}
                {isEditMode && hasUnsavedChanges && (
                  <button
                    onClick={handleSaveChanges}
                    disabled={updating}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-success-bg0 text-white hover:bg-success disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                )}

                {/* Export Button - Opens Export Dialog */}
                <button
                  onClick={() => setShowExportDialog(true)}
                  disabled={exporting}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-bw-primary-tint0 text-white hover:bg-bw-primary disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>

                {/* Regenerate */}
                {onRegenerateProposal && (
                  <button
                    onClick={handleRegenerateProposal}
                    disabled={regenerating}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
                  >
                    <RotateCw className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                  </button>
                )}

                {/* Mobile Nav */}
                <button
                  onClick={() => setShowMobileNav(!showMobileNav)}
                  className="sm:hidden p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
                >
                  <Menu className="h-5 w-5" />
                </button>

                {/* Close */}
                <button
                  onClick={onClose}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Section Tabs (for source-grounded and orchestrated proposals) */}
          {(isSourceGrounded || isOrchestrated) && (
            <div className="bg-slate-100 border-b border-slate-200 px-6">
              <div className="flex gap-1">
                {[
                  { id: 'content', label: 'Proposal Content', icon: FileText },
                  { id: 'gaps', label: 'Gap Report', icon: AlertTriangle },
                  ...(isSourceGrounded ? [{ id: 'sources', label: 'Source Map', icon: MapIcon }] : []),
                  ...(isSourceGrounded ? [{ id: 'evidence', label: 'Evidence Library', icon: Database }] : []),
                  { id: 'roadmap', label: 'Win Checklist', icon: CheckCircle },
                ].map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id as any)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                        activeSection === section.id
                          ? 'text-slate-900 bg-bw-surface border-t-2 border-blue-500 -mb-px'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}

                {/* Citations Toggle - only for source-grounded */}
                {isSourceGrounded && (
                  <div className="ml-auto flex items-center gap-2 py-2">
                    <span className="text-xs text-slate-500">Show Citations</span>
                    <button
                      onClick={() => setShowCitations(!showCitations)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${
                        showCitations ? 'bg-bw-primary-tint0' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-bw-surface rounded-full shadow transition-transform ${
                          showCitations ? 'translate-x-5' : ''
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Tabs */}
          {activeSection === 'content' && (
            <div className="hidden sm:block bg-bw-surface-subtle border-b border-bw-border px-6 overflow-x-auto">
              <nav className="flex gap-1 -mb-px">
                {contentTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                        isActive
                          ? 'border-blue-500 text-bw-primary'
                          : 'border-transparent text-bw-muted hover:text-bw-ink hover:border-bw-border'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          {/* Proposal Content */}
          {activeSection === 'content' && (
            <div className="p-6">
              {/* Section Header with Regenerate Button (for orchestrated proposals) */}
              {/* Don't show for special tabs: feedback, contractAnalysis, profileAnalysis, deliverables, product */}
              {isOrchestrated && activeTab !== 'feedback' && activeTab !== 'contractAnalysis' && activeTab !== 'profileAnalysis' && activeTab !== 'deliverables' && activeTab !== 'product' && !isEditMode && (
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-bw-border">
                  <h3 className="text-lg font-semibold text-bw-ink">
                    {getCurrentSectionTitle()}
                  </h3>
                  <div className="flex items-center gap-2">
                    {/* Profile Source Button */}
                    <button
                      onClick={() => setShowProfileSourceModal(true)}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-bw-primary bg-bw-primary-tint hover:bg-bw-primary-tint rounded-lg transition-colors"
                      title="See where this content comes from in your profile"
                    >
                      <Info className="h-4 w-4" />
                      Profile Source
                    </button>
                    {/* Enhance Section Button */}
                    <button
                      onClick={() => handleOpenFeedbackDialog(activeTab, getCurrentSectionTitle())}
                      disabled={regenerating}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-bw-primary bg-bw-primary-tint hover:bg-bw-primary-tint rounded-lg transition-colors disabled:opacity-50"
                    >
                      {regenerating && regeneratingSectionType === activeTab ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4" />
                          Enhance Section
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {isEditMode ? (
                <textarea
                  value={getContent(activeTab)}
                  onChange={(e) => {
                    const newContent = { ...editedContent };
                    switch (activeTab) {
                      case 'contractAnalysis':
                        newContent.contractAnalysis = e.target.value;
                        break;
                      case 'profileAnalysis':
                        newContent.profileAnalysis = e.target.value;
                        break;
                      case 'coverLetter':
                        newContent.coverLetter = e.target.value;
                        break;
                      case 'feedback':
                        newContent.feedback = e.target.value;
                        break;
                      default:
                        const idx = newContent.technicalSections.findIndex(
                          s => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab
                        );
                        if (idx !== -1) {
                          newContent.technicalSections[idx].content = e.target.value;
                        }
                    }
                    setEditedContent(newContent);
                    setHasUnsavedChanges(true);
                  }}
                  className="w-full h-[500px] p-4 font-mono text-sm border border-bw-border rounded-lg resize-none focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                  placeholder="Enter content here..."
                />
              ) : (
                <div className="prose prose-slate max-w-none">
                  {/* Product Tab - AI synthesizes what we're selling */}
                  {activeTab === 'product' && editedContent.deliverablesData ? (
                    <ProductSynthesis
                      productDefinition={editedContent.deliverablesData.product_definition}
                      onRegenerate={handleSynthesizeProduct}
                      onEnhance={handleOpenProductFeedback}
                      isRegenerating={regeneratingDeliverables}
                      isEnhancing={enhancingProductSection}
                    />
                  ) : activeTab === 'product' && !editedContent.deliverablesData ? (
                    <div className="text-center py-12 bg-bw-surface-subtle rounded-lg border border-bw-border">
                      <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="font-medium text-bw-ink">Product Synthesis Pending</p>
                      <p className="text-sm text-bw-muted mt-1 mb-4">Generate deliverables analysis first to synthesize the product definition.</p>
                      <button
                        onClick={handleRegenerateDeliverables}
                        disabled={regeneratingDeliverables}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-bw-primary hover:brightness-110 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        {regeneratingDeliverables ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Package className="h-4 w-4" />
                            Generate Deliverables & Product
                          </>
                        )}
                      </button>
                    </div>
                  ) : /* Deliverables Tab Content */
                  activeTab === 'deliverables' && editedContent.deliverablesData ? (
                    <DeliverablesSectionContent
                      deliverables={editedContent.deliverablesData}
                      onEnhanceSection={handleOpenIndividualDeliverableFeedback}
                      onEnhanceDeliverables={handleOpenDeliverablesFeedback}
                      onRegenerateDeliverables={handleRegenerateDeliverables}
                      isEnhancing={enhancingDeliverable}
                      enhancingId={enhancingDeliverableId || undefined}
                      isRegenerating={regeneratingDeliverables}
                      isEnhancingSection={enhancingDeliverableSection}
                    />
                  ) : activeTab === 'deliverables' && !editedContent.deliverablesData ? (
                    <div className="text-center py-12 bg-bw-surface-subtle rounded-lg border border-bw-border">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="font-medium text-bw-ink">Deliverables Analysis Pending</p>
                      <p className="text-sm text-bw-muted mt-1 mb-4">Analyze contract requirements and generate labor estimates with product synthesis.</p>
                      <button
                        onClick={handleRegenerateDeliverables}
                        disabled={regeneratingDeliverables}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-bw-primary hover:brightness-110 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        {regeneratingDeliverables ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Package className="h-4 w-4" />
                            Generate Deliverables
                          </>
                        )}
                      </button>
                    </div>
                  ) : /* Check if this is the AI Feedback tab with structured feedback */
                  activeTab === 'feedback' && draft.aiFeedback && draft.aiFeedback.overallAssessment ? (
                    <AIFeedbackPanel
                      feedback={draft.aiFeedback}
                      onNavigateToProfile={handleNavigateToProfile}
                    />
                  ) : activeTab === 'feedback' && draft.aiFeedback ? (
                    /* Simple AI Feedback format from edge function */
                    <SimpleFeedbackDisplay feedback={draft.aiFeedback} />
                  ) : isSourceGrounded && showCitations ? (
                    <CitationHighlight
                      content={activeTab === 'coverLetter'
                        ? formatCoverLetter(getContent(activeTab) || 'No content available.')
                        : smartNormalize(getContent(activeTab) || 'No content available.')}
                      citations={allCitations}
                      evidence={evidenceMap}
                      onNavigateToProfile={handleNavigateToProfile}
                      showCitations={showCitations}
                    />
                  ) : (
                    <ProposalSectionRenderer
                      sectionType={activeTab}
                      content={getContent(activeTab) || 'No content available.'}
                      showCitations={showCitations}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Gap Report */}
          {activeSection === 'gaps' && gapReport && (
            <div className="p-6 space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-bw-primary-tint rounded-lg p-4">
                  <p className="text-sm text-bw-primary font-medium">Overall Readiness</p>
                  <p className="text-3xl font-bold text-bw-primary">{gapReport.overallReadiness || 0}%</p>
                </div>
                <div className="bg-success-bg rounded-lg p-4">
                  <p className="text-sm text-success font-medium">Requirements Met</p>
                  <p className="text-3xl font-bold text-success">{gapReport.summary?.fullyMet || 0}</p>
                </div>
                <div className="bg-fit-moderate-bg rounded-lg p-4">
                  <p className="text-sm text-fit-moderate font-medium">Partial Matches</p>
                  <p className="text-3xl font-bold text-fit-moderate">{gapReport.summary?.partiallyMet || 0}</p>
                </div>
                <div className="bg-urgent-bg rounded-lg p-4">
                  <p className="text-sm text-urgent font-medium">Critical Gaps</p>
                  <p className="text-3xl font-bold text-urgent">{gapReport.summary?.criticalGapsCount || 0}</p>
                </div>
              </div>

              {/* Win Assessment */}
              {winAssessment && (
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-bw-primary" />
                    Win Assessment
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-slate-500 mb-2">Win Probability</p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-bw-primary rounded-full"
                            style={{ width: `${winAssessment.winProbabilityEstimate}%` }}
                          />
                        </div>
                        <span className="text-2xl font-bold text-slate-900">
                          {winAssessment.winProbabilityEstimate}%
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-2">Competitive Position</p>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                          winAssessment.competitivePosition === 'STRONG'
                            ? 'bg-success-bg text-success'
                            : winAssessment.competitivePosition === 'MODERATE'
                            ? 'bg-fit-moderate-bg text-fit-moderate'
                            : 'bg-urgent-bg text-urgent'
                        }`}
                      >
                        {winAssessment.competitivePosition.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Critical Gaps */}
              {gapReport.criticalGaps?.length > 0 && (
                <div className="bg-urgent-bg border border-urgent-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-urgent mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Critical Gaps ({gapReport.criticalGaps?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {gapReport.criticalGaps?.map((gap, i) => (
                      <div key={i} className="bg-bw-surface rounded-lg p-4 border border-red-100">
                        <p className="font-medium text-red-900">{gap.requirement}</p>
                        <p className="text-sm text-urgent mt-1">{gap.recommendation}</p>
                        {gap.profileAction && (
                          <button
                            onClick={() => handleNavigateToProfile(gap.profileAction.split('→')[1]?.trim().toLowerCase() || 'profile')}
                            className="flex items-center gap-2 mt-3 text-sm font-medium text-urgent hover:text-urgent"
                          >
                            <ExternalLink className="h-4 w-4" />
                            {gap.profileAction}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Improvement Roadmap */}
              {improvementRoadmap && (
                <div className="bg-bw-surface border border-bw-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Improvement Roadmap</h3>
                  <div className="space-y-6">
                    {improvementRoadmap.immediateActions?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-fit-moderate uppercase tracking-wide mb-3">
                          Immediate (Before Submission)
                        </h4>
                        <div className="space-y-2">
                          {improvementRoadmap.immediateActions.map((action, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                              <ArrowRight className="h-4 w-4 text-fit-moderate mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-sm text-slate-700">{action.action}</p>
                                {action.profileSection && (
                                  <button
                                    onClick={() => handleNavigateToProfile(action.profileSection!)}
                                    className="text-xs text-fit-moderate hover:text-fit-moderate mt-1"
                                  >
                                    Go to {action.profileSection} →
                                  </button>
                                )}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                action.effort === 'LOW' ? 'bg-success-bg text-success' :
                                action.effort === 'MEDIUM' ? 'bg-fit-moderate-bg text-fit-moderate' :
                                'bg-urgent-bg text-urgent'
                              }`}>
                                {action.effort}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {improvementRoadmap.shortTermActions?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-bw-primary uppercase tracking-wide mb-3">
                          Short Term (1-2 Weeks)
                        </h4>
                        <div className="space-y-2">
                          {improvementRoadmap.shortTermActions.map((action, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-bw-primary-tint rounded-lg">
                              <ArrowRight className="h-4 w-4 text-bw-primary mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-slate-700 flex-1">{action.action}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Strengths */}
              {gapReport.strengthsToHighlight?.length > 0 && (
                <div className="bg-success-bg border border-success-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-success mb-4 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Strengths to Highlight
                  </h3>
                  <div className="space-y-2">
                    {gapReport.strengthsToHighlight?.map((strength, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-bw-surface rounded-lg border border-green-100">
                        <code className="text-xs bg-success-bg text-success px-1.5 py-0.5 rounded">
                          {strength.evidenceId}
                        </code>
                        <p className="text-sm text-slate-700">{strength.strength}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gap Report for orchestrated proposals (when no source-grounded data) */}
          {activeSection === 'gaps' && !gapReport && orchestratedFeedback && (
            <div className="p-6 space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-bw-primary-tint rounded-lg p-4">
                  <p className="text-sm text-bw-primary font-medium">Readiness Score</p>
                  <p className="text-3xl font-bold text-bw-primary">{orchestratedFeedback.readinessScore}%</p>
                </div>
                <div className="bg-urgent-bg rounded-lg p-4">
                  <p className="text-sm text-urgent font-medium">Critical Gaps</p>
                  <p className="text-3xl font-bold text-urgent">{orchestratedFeedback.gaps?.critical?.length || 0}</p>
                </div>
                <div className="bg-fit-moderate-bg rounded-lg p-4">
                  <p className="text-sm text-fit-moderate font-medium">Recommended</p>
                  <p className="text-3xl font-bold text-fit-moderate">{orchestratedFeedback.gaps?.recommended?.length || 0}</p>
                </div>
                <div className="bg-success-bg rounded-lg p-4">
                  <p className="text-sm text-success font-medium">Optional</p>
                  <p className="text-3xl font-bold text-success">{orchestratedFeedback.gaps?.optional?.length || 0}</p>
                </div>
              </div>

              {/* Matches Overview */}
              {orchestratedFeedback.matches && Object.keys(orchestratedFeedback.matches).length > 0 && (
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5 text-bw-primary" />
                    Requirements Match
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {orchestratedFeedback.matches.naicsMatch !== undefined && (
                      <div className={`rounded-lg p-3 ${orchestratedFeedback.matches.naicsMatch ? 'bg-success-bg' : 'bg-urgent-bg'}`}>
                        <p className="text-xs font-medium text-bw-muted">NAICS Match</p>
                        <p className={`text-lg font-bold ${orchestratedFeedback.matches.naicsMatch ? 'text-success' : 'text-urgent'}`}>
                          {orchestratedFeedback.matches.naicsMatch ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                    {orchestratedFeedback.matches.setAsideMatch !== undefined && (
                      <div className={`rounded-lg p-3 ${orchestratedFeedback.matches.setAsideMatch ? 'bg-success-bg' : 'bg-urgent-bg'}`}>
                        <p className="text-xs font-medium text-bw-muted">Set-Aside Match</p>
                        <p className={`text-lg font-bold ${orchestratedFeedback.matches.setAsideMatch ? 'text-success' : 'text-urgent'}`}>
                          {orchestratedFeedback.matches.setAsideMatch ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                    {orchestratedFeedback.matches.clearanceMatch !== undefined && (
                      <div className={`rounded-lg p-3 ${orchestratedFeedback.matches.clearanceMatch ? 'bg-success-bg' : 'bg-urgent-bg'}`}>
                        <p className="text-xs font-medium text-bw-muted">Clearance Match</p>
                        <p className={`text-lg font-bold ${orchestratedFeedback.matches.clearanceMatch ? 'text-success' : 'text-urgent'}`}>
                          {orchestratedFeedback.matches.clearanceMatch ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                    {orchestratedFeedback.matches.geographicMatch !== undefined && (
                      <div className={`rounded-lg p-3 ${orchestratedFeedback.matches.geographicMatch ? 'bg-success-bg' : 'bg-urgent-bg'}`}>
                        <p className="text-xs font-medium text-bw-muted">Geographic Match</p>
                        <p className={`text-lg font-bold ${orchestratedFeedback.matches.geographicMatch ? 'text-success' : 'text-urgent'}`}>
                          {orchestratedFeedback.matches.geographicMatch ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Critical Gaps */}
              {orchestratedFeedback.gaps?.critical?.length > 0 && (
                <div className="bg-urgent-bg border border-urgent-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-urgent mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Critical Gaps ({orchestratedFeedback.gaps?.critical?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {orchestratedFeedback.gaps?.critical?.map((gap: any, i: number) => (
                      <div key={i} className="bg-bw-surface rounded-lg p-4 border border-red-100">
                        <p className="font-medium text-red-900">{gap.message || gap.field}</p>
                        <p className="text-sm text-urgent mt-1">{gap.section}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Gaps */}
              {orchestratedFeedback.gaps?.recommended?.length > 0 && (
                <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-fit-moderate mb-4 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Recommended Improvements ({orchestratedFeedback.gaps?.recommended?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {orchestratedFeedback.gaps?.recommended?.map((gap: any, i: number) => (
                      <div key={i} className="bg-bw-surface rounded-lg p-4 border border-yellow-100">
                        <p className="font-medium text-yellow-900">{gap.message || gap.field}</p>
                        <p className="text-sm text-fit-moderate mt-1">{gap.section}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Gaps - Good Shape */}
              {(orchestratedFeedback.gaps?.critical?.length || 0) === 0 && (orchestratedFeedback.gaps?.recommended?.length || 0) === 0 && (
                <div className="text-center py-8 bg-success-bg rounded-lg border border-green-200">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
                  <p className="font-medium text-success">Looking good!</p>
                  <p className="text-sm text-success mt-1">No critical or recommended gaps identified.</p>
                </div>
              )}
            </div>
          )}

          {/* Gap Report placeholder when no data available */}
          {activeSection === 'gaps' && !gapReport && !orchestratedFeedback && (
            <div className="p-6 text-center text-bw-muted">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Gap Report data not available for this proposal.</p>
            </div>
          )}

          {/* Source Map */}
          {activeSection === 'sources' && sourceMap && (
            <div className="p-6 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-500">Total Citations</p>
                  <p className="text-3xl font-bold text-slate-900">{sourceMap.totalClaims}</p>
                </div>
                <div className="bg-bw-primary-tint rounded-lg p-4">
                  <p className="text-sm text-bw-primary">Direct Evidence</p>
                  <p className="text-3xl font-bold text-bw-primary">{sourceMap.claimsWithEvidence}</p>
                </div>
                <div className="bg-bw-primary-tint rounded-lg p-4">
                  <p className="text-sm text-bw-primary">Inferred</p>
                  <p className="text-3xl font-bold text-bw-primary">{sourceMap.claimsWithInference}</p>
                </div>
                <div className="bg-success-bg rounded-lg p-4">
                  <p className="text-sm text-success">Traceability</p>
                  <p className="text-3xl font-bold text-success">{sourceMap.traceabilityScore}%</p>
                </div>
              </div>

              {/* Citation List */}
              <div className="bg-bw-surface border border-bw-border rounded-lg overflow-hidden">
                <div className="bg-bw-surface-subtle px-4 py-3 border-b border-bw-border">
                  <h3 className="font-medium text-bw-ink">All Citations</h3>
                </div>
                <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {sourceMap.allCitations.map((citation, i) => (
                    <div key={i} className="p-4 hover:bg-bw-surface-subtle">
                      <div className="flex items-start gap-3">
                        <code className={`text-xs px-2 py-1 rounded font-mono ${
                          citation.matchType === 'INFERRED'
                            ? 'bg-bw-primary-tint text-bw-primary'
                            : 'bg-bw-primary-tint text-bw-primary'
                        }`}>
                          [{citation.evidenceId}]
                        </code>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-bw-ink">{citation.evidenceSummary}</p>
                          <p className="text-xs text-bw-muted mt-1">
                            {citation.evidenceType} • {citation.matchType}
                          </p>
                          {citation.inferenceChain && (
                            <p className="text-xs text-bw-primary mt-1 flex items-center gap-1">
                              <Link2 className="h-3 w-3" />
                              {citation.inferenceChain}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Source Map placeholder for non-source-grounded */}
          {activeSection === 'sources' && !sourceMap && (
            <div className="p-6 text-center text-bw-muted">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Source Map data not available for this proposal.</p>
            </div>
          )}

          {/* Evidence Library */}
          {activeSection === 'evidence' && sourceGroundedOutput && (
            <div className="p-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-bw-primary-tint rounded-lg p-4">
                  <p className="text-sm text-bw-primary font-medium">Total Evidence</p>
                  <p className="text-3xl font-bold text-bw-primary">{sourceGroundedOutput.evidenceLibrary?.totalItems || 0}</p>
                </div>
                <div className="bg-success-bg rounded-lg p-4">
                  <p className="text-sm text-success font-medium">Used in Proposal</p>
                  <p className="text-3xl font-bold text-success">{sourceGroundedOutput.evidenceLibrary?.usedItems || 0}</p>
                </div>
                <div className="bg-bw-primary-tint rounded-lg p-4">
                  <p className="text-sm text-bw-primary font-medium">High Relevance</p>
                  <p className="text-3xl font-bold text-bw-primary">{sourceGroundedOutput.evidenceLibrary?.highRelevanceItems || 0}</p>
                </div>
                <div className="bg-fit-moderate-bg rounded-lg p-4">
                  <p className="text-sm text-fit-moderate font-medium">Inferred Capabilities</p>
                  <p className="text-3xl font-bold text-fit-moderate">{sourceGroundedOutput.evidenceLibrary?.inferredCapabilitiesCount || 0}</p>
                </div>
              </div>

              {/* Evidence Usage Progress */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Evidence Utilization</span>
                  <span className="text-sm text-slate-500">
                    {sourceGroundedOutput.evidenceLibrary?.usedItems || 0} of {sourceGroundedOutput.evidenceLibrary?.totalItems || 0} items used
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div
                    className="bg-bw-primary h-3 rounded-full transition-all"
                    style={{
                      width: `${sourceGroundedOutput.evidenceLibrary?.totalItems
                        ? Math.round((sourceGroundedOutput.evidenceLibrary.usedItems / sourceGroundedOutput.evidenceLibrary.totalItems) * 100)
                        : 0}%`
                    }}
                  />
                </div>
              </div>

              {/* Evidence by Citation */}
              <div className="bg-bw-surface border border-slate-200 rounded-lg">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <h4 className="font-medium text-slate-700 flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Evidence Used in Citations
                  </h4>
                </div>
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {(sourceMap?.allCitations || []).map((citation, i) => (
                    <div key={i} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-bw-primary-tint text-bw-primary text-xs font-mono rounded">
                              {citation.evidenceId}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              citation.matchType === 'DIRECT' ? 'bg-success-bg text-success' : 'bg-fit-moderate-bg text-fit-moderate'
                            }`}>
                              {citation.matchType}
                            </span>
                            <span className="text-xs text-slate-400">{citation.evidenceType}</span>
                          </div>
                          <p className="text-sm text-slate-600">{citation.evidenceSummary}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Evidence placeholder for non-source-grounded */}
          {activeSection === 'evidence' && !sourceGroundedOutput && (
            <div className="p-6 text-center text-bw-muted">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Evidence Library data not available for this proposal.</p>
            </div>
          )}

          {/* Win Checklist */}
          {activeSection === 'roadmap' && (
            <div className="p-6 space-y-6">
              {/* Summary Header */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Win Checklist for This Bid</h3>
                <p className="text-sm text-slate-600">
                  Prioritized actions to maximize your chances of winning. Focus on compliance issues first,
                  then high-impact improvements, then differentiators.
                </p>
              </div>

              {/* Compute items from improvementRoadmap OR gapReport OR orchestratedFeedback */}
              {(() => {
                // Get items from improvementRoadmap if available
                const criticalItems = improvementRoadmap?.criticalForCompliance || improvementRoadmap?.immediateActions || [];
                const highImpactItems = improvementRoadmap?.highImpactOnWin || improvementRoadmap?.shortTermActions || [];
                const differentiatorItems = improvementRoadmap?.differentiators || improvementRoadmap?.longTermActions || [];

                // Fallback: derive from gapReport if no roadmap items
                const fallbackCritical = criticalItems.length === 0 && gapReport?.criticalGaps
                  ? gapReport.criticalGaps.map(gap => ({
                      action: gap.recommendation,
                      impact: `Critical: ${gap.requirement}`,
                      effort: 'HIGH' as const,
                      profileSection: gap.profileAction?.split('→')[1]?.trim().toLowerCase() || undefined,
                    }))
                  : [];

                const fallbackHighImpact = highImpactItems.length === 0 && gapReport?.improvementOpportunities
                  ? gapReport.improvementOpportunities.map(opp => ({
                      action: opp.recommendation,
                      impact: opp.impactOnWinProbability || 'Improves evaluation score',
                      effort: 'MEDIUM' as const,
                      profileSection: opp.profileAction?.split('→')[1]?.trim().toLowerCase() || undefined,
                    }))
                  : [];

                // Additional fallback: use orchestratedFeedback gaps if available
                const orchestratedCritical = criticalItems.length === 0 && fallbackCritical.length === 0 && orchestratedFeedback?.gaps?.critical
                  ? orchestratedFeedback.gaps.critical.map((gap: any) => ({
                      action: gap.message || `Address ${gap.field}`,
                      impact: `Critical gap in ${gap.section || 'profile'}`,
                      effort: 'HIGH' as const,
                      profileSection: gap.section?.toLowerCase() || undefined,
                    }))
                  : [];

                const orchestratedRecommended = highImpactItems.length === 0 && fallbackHighImpact.length === 0 && orchestratedFeedback?.gaps?.recommended
                  ? orchestratedFeedback.gaps.recommended.map((gap: any) => ({
                      action: gap.message || `Improve ${gap.field}`,
                      impact: `Recommended improvement for ${gap.section || 'profile'}`,
                      effort: 'MEDIUM' as const,
                      profileSection: gap.section?.toLowerCase() || undefined,
                    }))
                  : [];

                // Check if actually in good shape based on winAssessment or orchestrated readiness
                const isActuallyGoodShape =
                  ((winAssessment?.winProbabilityEstimate || 0) >= 60 &&
                   winAssessment?.competitivePosition !== 'NOT_COMPETITIVE' &&
                   winAssessment?.competitivePosition !== 'WEAK' &&
                   (gapReport?.criticalGaps?.length || 0) === 0) ||
                  (orchestratedFeedback &&
                   orchestratedFeedback.readinessScore >= 70 &&
                   (orchestratedFeedback.gaps?.critical?.length || 0) === 0);

                const finalCritical = criticalItems.length > 0 ? criticalItems : (fallbackCritical.length > 0 ? fallbackCritical : orchestratedCritical);
                const finalHighImpact = highImpactItems.length > 0 ? highImpactItems : (fallbackHighImpact.length > 0 ? fallbackHighImpact : orchestratedRecommended);
                const finalDifferentiators = differentiatorItems;

                const hasAnyItems = finalCritical.length > 0 || finalHighImpact.length > 0 || finalDifferentiators.length > 0;

                return (
                  <>
                    {/* 🚨 MUST FIX: Critical for Compliance */}
                    {finalCritical.length > 0 && (
                      <div className="bg-urgent-bg border-2 border-red-300 rounded-lg">
                        <div className="px-4 py-3 border-b border-red-200 bg-urgent-bg">
                          <h4 className="font-semibold text-urgent flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5" />
                            🚨 MUST FIX — Critical for Compliance
                          </h4>
                          <p className="text-xs text-urgent mt-1">Without these, your proposal may be disqualified or non-responsive</p>
                        </div>
                        <div className="divide-y divide-red-100">
                          {finalCritical.map((action, i) => (
                            <div key={i} className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 bg-red-200 text-urgent rounded-full flex items-center justify-center text-sm font-bold">
                                  {i + 1}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-slate-800">{action.action}</p>
                                  <p className="text-sm text-slate-600 mt-1">{action.impact}</p>
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                      action.effort === 'LOW' ? 'bg-success-bg text-success' :
                                      action.effort === 'MEDIUM' ? 'bg-fit-moderate-bg text-fit-moderate' :
                                      'bg-urgent-bg text-urgent'
                                    }`}>
                                      {action.effort} EFFORT
                                    </span>
                                    {action.profileSection && (
                                      <button
                                        onClick={() => onNavigateToProfile?.(action.profileSection!)}
                                        className="text-xs text-bw-primary hover:text-bw-primary flex items-center gap-1 font-medium"
                                      >
                                        <ArrowRight className="h-3 w-3" />
                                        Update Profile → {action.profileSection}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ⚡ SHOULD FIX: High Impact on Win */}
                    {finalHighImpact.length > 0 && (
                      <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg">
                        <div className="px-4 py-3 border-b border-amber-200">
                          <h4 className="font-semibold text-fit-moderate flex items-center gap-2">
                            <Zap className="h-5 w-5" />
                            ⚡ SHOULD FIX — High Impact on Score
                          </h4>
                          <p className="text-xs text-fit-moderate mt-1">These weaknesses will hurt your evaluation score</p>
                        </div>
                        <div className="divide-y divide-amber-100">
                          {finalHighImpact.map((action, i) => (
                            <div key={i} className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 bg-amber-200 text-fit-moderate rounded-full flex items-center justify-center text-sm font-bold">
                                  {i + 1}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-slate-800">{action.action}</p>
                                  <p className="text-sm text-slate-600 mt-1">{action.impact}</p>
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                      action.effort === 'LOW' ? 'bg-success-bg text-success' :
                                      action.effort === 'MEDIUM' ? 'bg-fit-moderate-bg text-fit-moderate' :
                                      'bg-urgent-bg text-urgent'
                                    }`}>
                                      {action.effort} EFFORT
                                    </span>
                                    {action.profileSection && (
                                      <button
                                        onClick={() => onNavigateToProfile?.(action.profileSection!)}
                                        className="text-xs text-bw-primary hover:text-bw-primary flex items-center gap-1 font-medium"
                                      >
                                        <ArrowRight className="h-3 w-3" />
                                        Update Profile → {action.profileSection}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ✨ COULD ADD: Differentiators */}
                    {finalDifferentiators.length > 0 && (
                      <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg">
                        <div className="px-4 py-3 border-b border-blue-200">
                          <h4 className="font-semibold text-bw-primary flex items-center gap-2">
                            <Star className="h-5 w-5" />
                            ✨ COULD ADD — Competitive Differentiators
                          </h4>
                          <p className="text-xs text-bw-primary mt-1">Would set you apart from competitors</p>
                        </div>
                        <div className="divide-y divide-blue-100">
                          {finalDifferentiators.map((action, i) => (
                            <div key={i} className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-200 text-bw-primary rounded-full flex items-center justify-center text-sm font-bold">
                                  {i + 1}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-slate-800">{action.action}</p>
                                  <p className="text-sm text-slate-600 mt-1">{action.impact}</p>
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                      action.effort === 'LOW' ? 'bg-success-bg text-success' :
                                      action.effort === 'MEDIUM' ? 'bg-fit-moderate-bg text-fit-moderate' :
                                      'bg-urgent-bg text-urgent'
                                    }`}>
                                      {action.effort} EFFORT
                                    </span>
                                    {action.profileSection && (
                                      <button
                                        onClick={() => onNavigateToProfile?.(action.profileSection!)}
                                        className="text-xs text-bw-primary hover:text-bw-primary flex items-center gap-1 font-medium"
                                      >
                                        <ArrowRight className="h-3 w-3" />
                                        Update Profile → {action.profileSection}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Show warning if low win probability but no items found */}
                    {!hasAnyItems && !isActuallyGoodShape && (
                      <div className="text-center py-8 bg-fit-moderate-bg rounded-lg border border-amber-200">
                        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-fit-moderate" />
                        <p className="font-medium text-fit-moderate">Analysis indicates areas for improvement</p>
                        <p className="text-sm text-fit-moderate mt-1">
                          Win probability: {winAssessment?.winProbabilityEstimate || 0}% •
                          Position: {winAssessment?.competitivePosition || 'Unknown'}
                        </p>
                        <p className="text-sm text-fit-moderate mt-2">
                          Review your profile to strengthen evidence for this opportunity.
                        </p>
                      </div>
                    )}

                    {/* All Clear - only show if actually in good shape */}
                    {!hasAnyItems && isActuallyGoodShape && (
                      <div className="text-center text-bw-muted py-8 bg-success-bg rounded-lg border border-green-200">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
                        <p className="font-medium text-success">You're in great shape for this bid!</p>
                        <p className="text-sm text-success mt-1">No critical gaps or high-impact improvements identified.</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

        </div>

        {/* Mobile Nav Overlay */}
        {showMobileNav && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40 sm:hidden" onClick={() => setShowMobileNav(false)} />
            <div
              ref={mobileNavRef}
              className="fixed inset-y-0 right-0 w-72 bg-bw-surface shadow-xl z-50 sm:hidden overflow-y-auto"
            >
              <div className="p-4 border-b bg-bw-surface-subtle flex items-center justify-between">
                <h3 className="font-medium">Sections</h3>
                <button onClick={() => setShowMobileNav(false)}>
                  <X className="h-5 w-5 text-bw-muted" />
                </button>
              </div>
              <nav className="p-4 space-y-1">
                {contentTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setShowMobileNav(false);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg ${
                        activeTab === tab.id
                          ? 'bg-bw-primary-tint text-bw-primary'
                          : 'text-bw-ink hover:bg-bw-surface-subtle'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {tab.label}
                      {activeTab === tab.id && <ChevronRight className="h-4 w-4 ml-auto" />}
                    </button>
                  );
                })}
              </nav>
            </div>
          </>
        )}

        {/* Section Feedback Dialog for Regeneration */}
        <SectionFeedbackDialog
          isOpen={showFeedbackDialog}
          onClose={() => {
            setShowFeedbackDialog(false);
            setRegeneratingSectionType(null);
          }}
          onSubmit={handleRegenerateSection}
          sectionTitle={regeneratingSectionTitle}
          sectionType={regeneratingSectionType || ''}
          isSubmitting={regenerating}
        />

        {/* Profile Source Modal */}
        <ProfileSourceModal
          isOpen={showProfileSourceModal}
          onClose={() => setShowProfileSourceModal(false)}
          sectionType={activeTab}
          sectionTitle={getCurrentSectionTitle()}
          profile={profile}
        />

        {/* Deliverables Section Enhancement Dialog */}
        <DeliverableFeedbackDialog
          isOpen={showDeliverableFeedbackDialog}
          onClose={() => setShowDeliverableFeedbackDialog(false)}
          onSubmit={handleEnhanceDeliverablesSection}
          mode="section"
          isSubmitting={enhancingDeliverableSection}
        />

        {/* Individual Deliverable Enhancement Dialog */}
        <DeliverableFeedbackDialog
          isOpen={showIndividualDeliverableFeedback}
          onClose={() => {
            setShowIndividualDeliverableFeedback(false);
            setSelectedDeliverableForEnhancement(null);
          }}
          onSubmit={handleEnhanceIndividualDeliverable}
          mode="individual"
          deliverableId={selectedDeliverableForEnhancement?.id}
          deliverableName={selectedDeliverableForEnhancement?.name}
          isSubmitting={enhancingDeliverable}
        />

        {/* Product Section Enhancement Dialog */}
        <ProductFeedbackDialog
          isOpen={showProductFeedbackDialog}
          onClose={() => setShowProductFeedbackDialog(false)}
          onSubmit={handleEnhanceProductSection}
          productName={editedContent.deliverablesData?.product_definition?.product_name}
          isSubmitting={enhancingProductSection}
        />
      </div>
    </div>

    {/* Export Dialog */}
    {showExportDialog && contract && (
      <ExportDialog
        proposal={editedContent}
        contract={contract}
        onClose={() => setShowExportDialog(false)}
        showCitations={showCitations}
      />
    )}
    </>
  );
}
